<template>
  <div class="orders-page">
    <el-dialog
      title="查询订单"
      :visible.sync="queryDialogVisible"
      width="420px"
      custom-class="order-query-dialog"
      :show-close="true"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :before-close="closeQueryDialog"
    >
      <div class="query-form" @keyup.enter="submitOrderQuery">
        <label class="query-field">
          <span>联系电话</span>
          <el-input
            v-model.trim="queryForm.receiverPhone"
            placeholder="请输入下单手机号"
            autocomplete="off"
          />
        </label>
        <p v-if="queryErrorMessage" class="query-error">{{ queryErrorMessage }}</p>
      </div>
      <template #footer>
        <button type="button" class="primary-button query-submit" :disabled="loading" @click="submitOrderQuery">
          {{ loading ? '查询中' : '查询订单' }}
        </button>
      </template>
    </el-dialog>

    <section class="orders-panel">
      <div class="orders-header">
        <div>
          <p class="eyebrow">订单中心</p>
          <h1>我的订单</h1>
          <p class="summary">查看预约购买记录和订单详情。</p>
        </div>
      </div>

      <div v-if="loading" class="state-card">
        <div class="spinner" aria-hidden="true"></div>
        <p>正在加载订单信息...</p>
      </div>

      <div v-else-if="errorMessage" class="state-card error-card">
        <h2>订单加载失败</h2>
        <p>{{ errorMessage }}</p>
        <button type="button" class="primary-button" @click="fetchOrders()">重新加载</button>
      </div>

      <div v-else-if="!hasQueried" class="empty-state">
        <div class="empty-icon" aria-hidden="true">
          <span></span>
        </div>
        <h2>请先查询订单</h2>
        <p>输入联系电话后，可以查看对应订单。</p>
        <button type="button" class="primary-button" @click="queryDialogVisible = true">查询订单</button>
      </div>

      <div v-else-if="orders.length === 0" class="empty-state">
        <div class="empty-icon" aria-hidden="true">
          <span></span>
        </div>
        <h2>暂无订单记录</h2>
        <p>完成预约购买后，订单信息会在这里展示。</p>
        <button type="button" class="primary-button" @click="$router.push('/buy')">去预约购买</button>
      </div>

      <template v-else>
        <div class="orders-summary">
          <strong>共 {{ pagination.total }} 笔订单</strong>
          <span>第 {{ pagination.pageNum }} / {{ pagination.pages || 1 }} 页</span>
        </div>

        <div class="order-list">
          <article v-for="order in orders" :key="order.id || order.orderSn" class="order-card">
            <div class="order-top">
              <div class="order-title">
                <h2>订单号：{{ order.orderSn || '-' }}</h2>
                <p>交易号：{{ order.transactionId || '-' }}</p>
              </div>
              <div class="amount-box">
                <span>实付金额</span>
                <strong>¥ {{ formatAmount(order.totalAmount) }}</strong>
              </div>
            </div>

            <div class="detail-grid">
              <div class="detail-item time-item">
                <span class="detail-icon" aria-hidden="true">
                  <svg viewBox="0 0 1024 1024" focusable="false">
                    <path d="M512 70.4a441.6 441.6 0 1 0 0 883.2 441.6 441.6 0 0 0 0-883.2z m0 64a377.6 377.6 0 1 1 0 755.2 377.6 377.6 0 0 1 0-755.2z" fill="currentColor"></path>
                    <path d="M544 480V256a32 32 0 1 0-64 0v230.4c0 31.7952 25.8048 57.6 57.6 57.6H768a32 32 0 1 0 0-64h-224z" fill="currentColor"></path>
                  </svg>
                </span>
                <div class="detail-copy">
                  <span>下单时间</span>
                  <strong>{{ formatTime(order.createTime) }}</strong>
                </div>
              </div>
              <div class="detail-item">
                <span class="detail-icon" aria-hidden="true">
                  <svg viewBox="0 0 1024 1024" focusable="false">
                    <path d="M610.919609 1004.488841h-196.678132c-186.563169 0-333.904363 0-333.904363-96.771497v-19.346314c0-181.348521 149.807734-328.865823 333.904363-328.865823h196.678132c184.096629 0 333.905387 147.516278 333.905387 328.865823v19.346314c-0.035836 96.771498-154.776649 96.771498-333.905387 96.771497z m-196.712944-386.977458c-152.099189 0-275.864399 121.473755-275.864399 270.823811v19.348361c0 38.728462 159.42816 38.728462 275.864399 38.728463H610.883773c96.383445 0 275.864399 0 275.864399-38.728463v-19.348361c0-149.350057-123.76521-270.823812-275.864399-270.823811H414.206665z m88.524101-77.423137c-146.17806 0-265.045003-117.175484-265.045003-261.203385S356.552706 17.715264 502.730766 17.715264s265.045003 117.140672 265.045003 261.168573S648.874014 540.088246 502.730766 540.088246z m-207.002992-261.203385c0 111.994625 92.859232 203.162398 207.002992 203.162398s207.002992-91.132961 207.002992-203.162398c0-112.030461-92.859232-203.127586-207.002992-203.127586s-207.002992 91.131937-207.002992 203.127586z" fill="currentColor"></path>
                  </svg>
                </span>
                <div class="detail-copy">
                  <span>下单人</span>
                  <strong>{{ order.receiverName || '-' }}</strong>
                </div>
              </div>
              <div class="detail-item">
                <span class="detail-icon" aria-hidden="true">
                  <svg viewBox="0 0 1024 1024" focusable="false">
                    <path d="M716.2 928.2h-7.7C351.2 923.9 63.9 625.4 68.3 262.7c0.1-5.2 0.3-12.1 0.8-19.5 2.5-45.2 31.8-83.6 74.7-97.8l125-41.5c27.7-9.2 57.3-7 83.4 6 26.1 13.1 45.5 35.6 54.7 63.3L442.5 281c18.9 57.1-12.1 119-69.2 138-6 2-10.8 6.4-13.3 12.3-1.5 3.7-2.8 9.4 0.2 15.5 45.8 95.7 128.7 170.4 227.5 204.9 10.3 3.6 21.5-1 26.4-10.6l26.8-53.4c13.1-26.1 35.5-45.5 63.2-54.7 27.7-9.2 57.3-7.1 83.4 6.1l101 50.7c26.2 13.2 45.7 35.7 54.8 63.6 9.1 27.8 6.8 57.5-6.4 83.6-21.2 41.6-48.9 95.8-66 128.9-16.9 32.6-48.8 54.5-85.4 58.6-22.8 2.5-46 3.7-69.3 3.7zM303.1 186.3c-2 0-4.3 0.3-6.7 1.1l-125 41.5c-8.2 2.7-14 10.4-14.5 19.1-0.3 6-0.6 11.6-0.6 15.9-3.7 314.1 244.4 572.6 553.3 576.3 22.5 0.3 44.5-0.8 66.1-3.2 7.3-0.8 13.6-5.1 16.9-11.5 17-33 44.6-87 65.7-128.5 3.5-6.8 2.3-13.1 1.3-16.2-1-3.1-3.8-8.9-10.7-12.3l-101-50.7c-6.8-3.4-13-2.2-16.2-1.2-3.1 1-8.8 3.8-12.3 10.6l-26.8 53.5c-24.6 49-82.2 72.3-134.1 54.2-120.7-42.2-222-133.3-277.8-250-13.2-27.5-13.8-59.6-1.8-88 12.3-29 36.6-51.4 66.5-61.4 11.1-3.7 17.1-15.7 13.4-26.8L323 200.8c-2.4-7.2-7.7-10.8-10.6-12.3-1.8-0.9-5.1-2.2-9.3-2.2z" fill="currentColor"></path>
                  </svg>
                </span>
                <div class="detail-copy">
                  <span>联系电话</span>
                  <strong>{{ order.receiverPhone || '-' }}</strong>
                </div>
              </div>
              <div class="detail-item">
                <span class="detail-icon" aria-hidden="true">
                  <svg viewBox="0 0 1024 1024" focusable="false">
                    <path d="M237.358431 284.464797l131.472334 375.310851-27.569916-19.554358L890.249275 640.22129c16.136515 0 29.212322 13.0799 29.212322 29.213345 0 16.129352-13.075807 29.205159-29.212322 29.205159L341.259826 698.639794c-12.409634 0-23.465434-7.836479-27.566846-19.549242L109.05016 94.8963l27.567869 19.553335L77.586564 114.449635c-16.129352 0-29.207206-13.074783-29.207206-29.212322 0-16.129352 13.077853-29.207206 29.207206-29.207206l59.032488 0c12.409634 0 23.466458 7.842619 27.566846 19.555381l52.728922 150.525272 710.724017 0c18.48705 0 32.326243 16.962324 28.612665 35.077913l-46.633087 227.389894c-2.547009 12.408611-12.796444 21.75549-25.382087 23.160489l-431.515944 48.065715c-16.036231 1.786693-30.482245-9.761318-32.266891-25.797549-1.783623-16.030092 9.764388-30.475082 25.798573-32.257681l410.798087-47.145763c0 0 20.75879-96.119151 35.926234-170.074513C893.311007 282.900162 362.038058 284.149619 237.358431 284.464797L237.358431 284.464797zM407.438061 818.372759c23.362081 0 42.36897 19.004843 42.36897 42.3659 0 23.360034-19.006889 42.364877-42.36897 42.364877-23.360034 0-42.363853-19.004843-42.363853-42.364877C365.073184 837.377602 384.078027 818.372759 407.438061 818.372759M407.438061 762.594385c-54.202483 0-98.142228 43.941791-98.142228 98.144274 0 54.207599 43.939745 98.143251 98.142228 98.143251s98.147344-43.936675 98.147344-98.143251C505.584382 806.536176 461.640544 762.594385 407.438061 762.594385L407.438061 762.594385zM816.372707 818.372759c23.357987 0 42.360783 19.004843 42.360783 42.3659 0 23.360034-19.002796 42.364877-42.360783 42.364877-23.360034 0-42.364877-19.004843-42.364877-42.364877C774.007831 837.377602 793.012673 818.372759 816.372707 818.372759M816.372707 762.594385c-54.206576 0-98.143251 43.941791-98.143251 98.144274 0 54.207599 43.937698 98.143251 98.143251 98.143251 54.200436 0 98.139158-43.936675 98.139158-98.143251C914.512888 806.536176 870.573143 762.594385 816.372707 762.594385L816.372707 762.594385zM816.372707 958.88191" fill="currentColor"></path>
                  </svg>
                </span>
                <div class="detail-copy">
                  <span>购买数量</span>
                  <strong>{{ order.quantity || 0 }} 台</strong>
                </div>
              </div>
              <div class="detail-item address-item">
                <span class="detail-icon" aria-hidden="true">
                  <svg viewBox="0 0 1024 1024" focusable="false">
                    <path d="M511.744 68.266667c-173.5168 0-314.026667 136.311467-314.7776 305.937066 0 60.910933 18.1248 118.903467 51.7632 168.465067l3.293867 4.693333 1.911466 3.1744 1.570134 2.389334c1.058133 1.553067 2.184533 3.037867 3.447466 4.5056l0.785067 0.853333 200.174933 232.823467a68.266667 68.266667 0 0 0 103.645867-0.170667L762.641067 558.08l-1.314134 1.450667a50.346667 50.346667 0 0 0 5.341867-6.621867l1.536-2.3552c0.631467-0.989867 1.860267-3.072 1.826133-3.003733 35.293867-49.322667 55.0912-109.431467 55.825067-172.782934C825.856 204.9536 684.970667 68.266667 511.744 68.266667z m0 68.266666c135.970133 0 245.845333 106.5984 245.845333 237.824a235.400533 235.400533 0 0 1-43.9808 134.775467l-2.952533 4.676267-198.997333 232.789333-200.192-232.823467-1.928534-3.191466-0.989866-1.450667a230.229333 230.229333 0 0 1-43.3152-134.775467C265.8304 242.858667 375.415467 136.533333 511.744 136.533333z" fill="currentColor"></path>
                    <path d="M783.803733 714.734933a34.133333 34.133333 0 0 1 45.243734 10.018134l1.4336 2.2528 73.386666 125.730133a68.266667 68.266667 0 0 1-54.784 102.5536l-4.5568 0.119467-666.043733-3.6352a68.266667 68.266667 0 0 1-60.654933-98.850134l2.133333-3.9424 69.9392-119.261866a34.133333 34.133333 0 0 1 60.16 32.170666l-1.262933 2.3552-69.9392 119.261867 666.043733 3.6352-73.386667-125.730133a34.133333 34.133333 0 0 1 12.288-46.677334z" fill="currentColor"></path>
                    <path d="M512 243.950933a136.533333 136.533333 0 1 0 0.034133 273.1008A136.533333 136.533333 0 0 0 512 243.950933z m0 68.266667a68.266667 68.266667 0 1 1-0.034133 136.567467A68.266667 68.266667 0 0 1 512 312.2176z" fill="currentColor"></path>
                  </svg>
                </span>
                <div class="detail-copy">
                  <span>收货地址</span>
                  <strong>{{ order.receiverAddress || '-' }}</strong>
                </div>
              </div>
              <div class="detail-item express-item">
                <span class="detail-icon" aria-hidden="true">
                  <svg viewBox="0 0 1024 1024" focusable="false">
                    <path d="M512 967.68c-20.48 0-35.84-5.12-51.2-10.24l-312.32-153.6c-20.48-10.24-35.84-25.6-46.08-40.96-10.24-20.48-15.36-40.96-15.36-61.44V327.68c-5.12-20.48 0-46.08 15.36-61.44 10.24-20.48 25.6-30.72 46.08-40.96L296.96 153.6s5.12 0 5.12-5.12L460.8 66.56c30.72-15.36 71.68-15.36 102.4 0l312.32 153.6c20.48 10.24 35.84 25.6 46.08 40.96 10.24 20.48 15.36 40.96 15.36 61.44v368.64c0 20.48-5.12 40.96-15.36 61.44-10.24 20.48-25.6 30.72-46.08 40.96l-312.32 153.6c-15.36 20.48-35.84 20.48-51.2 20.48zM158.72 337.92v358.4c0 5.12 0 15.36 5.12 20.48l15.36 15.36 291.84 143.36V496.64L158.72 337.92z m394.24 158.72v378.88l291.84-143.36 15.36-15.36c5.12-5.12 5.12-15.36 5.12-20.48v-358.4L727.04 409.6l-174.08 87.04zM209.92 276.48l307.2 153.6 107.52-56.32-307.2-153.6-107.52 56.32z m194.56-97.28l307.2 153.6L819.2 276.48l-286.72-143.36c-10.24-5.12-25.6-5.12-35.84 0l-92.16 46.08z" fill="currentColor"></path>
                  </svg>
                </span>
                <div class="detail-copy">
                  <span>物流信息</span>
                  <strong>{{ getExpressText(order) }}</strong>
                </div>
              </div>
            </div>
          </article>
        </div>

        <div v-if="pagination.total > pagination.pageSize" class="pagination-wrap">
          <el-pagination
            background
            layout="prev, pager, next"
            :current-page="pagination.pageNum"
            :page-size="pagination.pageSize"
            :total="pagination.total"
            @current-change="handlePageChange"
          />
        </div>
      </template>
    </section>
  </div>
</template>

<script>
import { getOrderStatusHistoryList } from '@/api/order'

const DEFAULT_PAGE_SIZE = 1

export default {
  name: 'MyOrders',
  data() {
    return {
      loading: false,
      errorMessage: '',
      queryDialogVisible: true,
      queryErrorMessage: '',
      hasQueried: false,
      queryForm: {
        receiverPhone: ''
      },
      orders: [],
      pagination: {
        pageNum: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        pages: 1,
        total: 0
      }
    }
  },
  methods: {
    closeQueryDialog(done) {
      if (typeof done === 'function') {
        done()
      } else {
        this.queryDialogVisible = false
      }

      if (this.$route.path !== '/buy') {
        this.$router.push('/buy')
      }
    },
    submitOrderQuery() {
      const receiverPhone = this.queryForm.receiverPhone.trim()

      if (!receiverPhone) {
        this.queryErrorMessage = '请输入联系电话'
        return
      }

      if (!/^1[3-9]\d{9}$/.test(receiverPhone)) {
        this.queryErrorMessage = '请输入正确的手机号'
        return
      }

      this.queryErrorMessage = ''
      this.queryForm.receiverPhone = receiverPhone
      this.fetchOrders(1)
    },
    async fetchOrders(pageNum = this.pagination.pageNum) {
      this.loading = true
      this.errorMessage = ''
      this.hasQueried = true

      try {
        const response = await getOrderStatusHistoryList({
          phone: this.queryForm.receiverPhone,
          page: pageNum,
          size: this.pagination.pageSize
        })

        this.loadOrdersFromResponse(response, pageNum)
        this.queryDialogVisible = false
      } catch (error) {
        this.orders = []
        this.queryDialogVisible = false
        this.errorMessage = error && error.message ? error.message : '订单接口请求失败'
      } finally {
        this.loading = false
      }
    },
    handlePageChange(pageNum) {
      this.fetchOrders(pageNum)
    },
    loadOrdersFromResponse(response, pageNum) {
      const data = response && response.data ? response.data : response
      const pageInfo = this.getPageInfo(data)
      const histories = data && data.histories && typeof data.histories === 'object' ? data.histories : {}
      const rawList = Array.isArray(pageInfo.list) ? pageInfo.list : []
      const total = this.toPositiveNumber(pageInfo.total, rawList.length)
      const currentPage = this.toPositiveNumber(pageNum, 1)
      const pageSize = DEFAULT_PAGE_SIZE
      const pages = Math.max(1, Math.ceil(total / pageSize))

      this.orders = rawList.map((order) => ({
        ...order,
        expressRemark: this.getOrderExpressRemark(order, histories)
      }))

      this.pagination = {
        pageNum: currentPage,
        pageSize,
        pages,
        total
      }
    },
    getPageInfo(data) {
      if (data && data.pageInfo) {
        return data.pageInfo
      }

      if (data && Array.isArray(data.list)) {
        return data
      }

      if (Array.isArray(data)) {
        return {
          list: data,
          pageNum: 1,
          pageSize: data.length || DEFAULT_PAGE_SIZE,
          pages: 1,
          total: data.length
        }
      }

      return {
        list: [],
        pageNum: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        pages: 1,
        total: 0
      }
    },
    getOrderExpressRemark(order, histories) {
      const transactionId = order && (order.transactionId || order.transaction_id)

      if (!transactionId) {
        return ''
      }

      const historyGroup = histories[transactionId]

      if (!historyGroup || Array.isArray(historyGroup)) {
        return ''
      }

      return historyGroup.remark || ''
    },
    getExpressText(order) {
      if (!order) {
        return '暂无物流'
      }

      const expressCompany = order.expressCompany
      const expressSn = order.expressSn
      const expressRemark = order.expressRemark

      if (expressCompany && expressSn) {
        return expressCompany + ' / ' + expressSn
      }

      if (expressCompany) {
        return expressCompany
      }

      if (expressSn) {
        return expressSn
      }

      if (expressRemark) {
        return expressRemark
      }

      return '暂无物流'
    },
    formatAmount(value) {
      const amount = Number(value)

      return Number.isFinite(amount) ? amount.toFixed(2) : '0.00'
    },
    formatTime(value) {
      const timestamp = this.toTimestamp(value)

      if (!timestamp) {
        return '-'
      }

      const date = new Date(timestamp)

      if (Number.isNaN(date.getTime())) {
        return '-'
      }

      const pad = (number) => String(number).padStart(2, '0')

      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
    },
    toTimestamp(value) {
      const timestamp = Number(value)

      return Number.isFinite(timestamp) ? timestamp : 0
    },
    toPositiveNumber(value, fallback) {
      const number = Number(value)

      return Number.isFinite(number) && number > 0 ? number : fallback
    }
  }
}
</script>

<style scoped>
.orders-page {
  min-height: calc(100vh - var(--jhy-header-h));
  padding: 56px clamp(16px, 5vw, 88px);
  background: #f7faf8;
  box-sizing: border-box;
}

.orders-panel {
  max-width: 1040px;
  margin: 0 auto;
}

.orders-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 24px;
}

.eyebrow {
  margin: 0 0 8px;
  color: #15803d;
  font-size: 15px;
  font-weight: 700;
}

.orders-header h1 {
  margin: 0;
  color: #1f2937;
  font-size: 34px;
  line-height: 1.2;
}

.summary {
  margin: 12px 0 0;
  color: #6b7280;
  font-size: 16px;
  line-height: 1.7;
}

.primary-button {
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 6px;
  background: #15803d;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;
}

.primary-button:hover {
  background: #116c33;
  transform: translateY(-1px);
}

.orders-page ::v-deep .order-query-dialog {
  border-radius: 8px;
}

.orders-page ::v-deep .order-query-dialog .el-dialog__header {
  padding: 22px 24px 10px;
}

.orders-page ::v-deep .order-query-dialog .el-dialog__title {
  color: #111827;
  font-size: 20px;
  font-weight: 800;
}

.orders-page ::v-deep .order-query-dialog .el-dialog__body {
  padding: 12px 24px 4px;
}

.orders-page ::v-deep .order-query-dialog .el-dialog__footer {
  display: flex;
  gap: 12px;
  padding: 16px 24px 24px;
}

.query-form {
  display: grid;
  gap: 16px;
}

.query-field {
  display: grid;
  gap: 8px;
}

.query-field span {
  color: #374151;
  font-size: 14px;
  font-weight: 700;
}

.query-error {
  margin: 0;
  color: #dc2626;
  font-size: 13px;
  line-height: 1.5;
}

.query-submit {
  flex: 1;
}

.query-submit:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  transform: none;
}

.state-card,
.empty-state {
  display: flex;
  min-height: 360px;
  padding: 48px 24px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-sizing: border-box;
}

.state-card p,
.empty-state p {
  margin: 0 0 24px;
  color: #6b7280;
  font-size: 15px;
  line-height: 1.7;
}

.error-card h2,
.empty-state h2 {
  margin: 0 0 10px;
  color: #1f2937;
  font-size: 24px;
}

.spinner {
  width: 34px;
  height: 34px;
  margin-bottom: 16px;
  border: 3px solid #d1fae5;
  border-top-color: #15803d;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.empty-icon {
  width: 68px;
  height: 68px;
  margin-bottom: 20px;
  border-radius: 50%;
  background: #eaf7ef;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-icon span {
  width: 30px;
  height: 36px;
  border: 2px solid #15803d;
  border-radius: 4px;
  position: relative;
}

.empty-icon span::before,
.empty-icon span::after {
  content: "";
  position: absolute;
  left: 7px;
  right: 7px;
  height: 2px;
  background: #15803d;
  border-radius: 2px;
}

.empty-icon span::before {
  top: 10px;
}

.empty-icon span::after {
  top: 20px;
}

.orders-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 28px 0 18px;
  color: #111827;
  font-size: 14px;
}

.orders-summary strong {
  color: #111827;
  font-size: 20px;
  font-weight: 800;
}

.order-list {
  display: grid;
  gap: 16px;
}

.order-card {
  padding: 28px 32px;
  background: #ffffff;
  border: 1px solid #d9e1e8;
  border-radius: 8px;
  box-sizing: border-box;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.04);
}

.order-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 28px;
  padding-bottom: 26px;
  border-bottom: 1px solid #e5e7eb;
}

.order-title {
  min-width: 0;
}

.order-title h2 {
  margin: 0 0 6px;
  color: #111827;
  font-size: 18px;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.order-title p {
  margin: 0;
  color: #6b7280;
  font-size: 13px;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.amount-box {
  min-width: 128px;
  min-height: 96px;
  padding: 16px 18px;
  display: grid;
  align-content: center;
  justify-items: center;
  gap: 10px;
  border-radius: 8px;
  background: #eaf9ef;
  box-sizing: border-box;
}

.amount-box span {
  color: #6b7280;
  font-size: 13px;
}

.amount-box strong {
  color: #15803d;
  font-size: 24px;
  line-height: 1.2;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0;
  padding-top: 24px;
}

.detail-item {
  position: relative;
  display: grid;
  grid-template-columns: 22px minmax(0, 1fr);
  gap: 12px;
  align-items: start;
  min-width: 0;
  padding: 0 28px 24px 0;
  box-sizing: border-box;
}

.detail-item:not(:nth-child(4n + 1)) {
  padding-left: 28px;
  border-left: 1px solid #e5e7eb;
}

.detail-item:nth-child(n + 5) {
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
}

.detail-icon {
  width: 20px;
  height: 20px;
  margin-top: 2px;
  color: #334155;
}

.detail-icon svg {
  display: block;
  width: 100%;
  height: 100%;
}

.detail-copy {
  display: grid;
  gap: 8px;
  min-width: 0;
}

.detail-copy span {
  color: #6b7280;
  font-size: 14px;
  line-height: 1.2;
}

.detail-copy strong {
  color: #111827;
  font-size: 16px;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.address-item {
  grid-column: span 2;
}

.express-item {
  grid-column: span 2;
}

.pagination-wrap {
  display: flex;
  justify-content: center;
  margin-top: 24px;
}

.pagination-wrap ::v-deep .el-pagination.is-background .el-pager li:not(.disabled).active {
  background-color: #15803d;
  color: #ffffff;
}

.pagination-wrap ::v-deep .el-pagination.is-background .el-pager li:not(.disabled):not(.active):hover {
  background-color: #ffffff;
  color: #15803d;
}

.pagination-wrap ::v-deep .el-pagination.is-background .btn-prev:hover,
.pagination-wrap ::v-deep .el-pagination.is-background .btn-next:hover {
  background-color: #ffffff;
  color: #15803d;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1024px) {
  .detail-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .detail-item {
    padding-left: 0;
  }

  .detail-item:not(:nth-child(4n + 1)) {
    border-left: 0;
  }

  .detail-item:nth-child(even) {
    padding-left: 28px;
    border-left: 1px solid #e5e7eb;
  }

  .detail-item:nth-child(n + 3) {
    padding-top: 24px;
    border-top: 1px solid #e5e7eb;
  }

  .address-item,
  .express-item {
    grid-column: span 2;
  }
}

@media (max-width: 767px) {
  .orders-page {
    padding: 22px 12px 30px;
  }

  .orders-header {
    align-items: stretch;
    flex-direction: column;
    margin-bottom: 10px;
  }

  .orders-header h1 {
    font-size: 24px;
  }

  .state-card,
  .empty-state {
    min-height: 300px;
    padding: 36px 18px;
  }

  .orders-summary,
  .order-top {
    align-items: stretch;
    flex-direction: column;
  }

  .orders-summary {
    margin: 16px 0 12px;
    gap: 6px;
  }

  .orders-summary strong {
    font-size: 17px;
  }

  .orders-summary span {
    font-size: 13px;
  }

  .order-card {
    padding: 16px;
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.05);
  }

  .order-top {
    gap: 14px;
    padding-bottom: 16px;
  }

  .order-title h2 {
    margin: 0 0 4px;
    font-size: 15px;
    line-height: 1.45;
  }

  .order-title p {
    font-size: 12px;
  }

  .amount-box {
    min-width: 0;
    min-height: auto;
    width: 100%;
    padding: 10px 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .amount-box span {
    font-size: 12px;
  }

  .amount-box strong {
    font-size: 20px;
  }

  .detail-grid {
    grid-template-columns: 1fr;
    padding-top: 4px;
  }

  .detail-item,
  .detail-item:nth-child(even),
  .detail-item:not(:nth-child(4n + 1)) {
    grid-template-columns: 22px minmax(0, 1fr);
    gap: 10px;
    padding: 12px 0;
    border-left: 0;
  }

  .detail-item:first-child {
    padding-top: 0;
  }

  .detail-item:nth-child(n + 3) {
    padding-top: 12px;
  }

  .detail-item:nth-child(n + 2) {
    border-top: 1px solid #e5e7eb;
  }

  .detail-icon {
    width: 18px;
    height: 18px;
    margin-top: 1px;
  }

  .detail-copy {
    display: grid;
    grid-template-columns: 72px minmax(0, 1fr);
    align-items: start;
    gap: 8px;
    min-width: 0;
  }

  .detail-copy span {
    font-size: 13px;
    line-height: 1.35;
  }

  .detail-copy strong {
    min-width: 0;
    font-size: 14px;
    line-height: 1.45;
  }

  .address-item {
    grid-column: auto;
  }

  .express-item {
    grid-column: auto;
  }

  .pagination-wrap {
    margin-top: 18px;
  }

  .pagination-wrap ::v-deep .el-pagination {
    display: flex;
    justify-content: center;
    white-space: normal;
  }

  .pagination-wrap ::v-deep .el-pagination.is-background .btn-prev,
  .pagination-wrap ::v-deep .el-pagination.is-background .btn-next,
  .pagination-wrap ::v-deep .el-pagination.is-background .el-pager li {
    min-width: 34px;
    height: 34px;
    line-height: 34px;
    margin: 0 4px;
  }
}
</style>
